import type { DailySeries } from "../model/dailySeries.ts";
import type { AquiferLayer, OutletPointLayer, SubBasinLayer } from "../model/gisLayer.ts";
import type { GenericLayer } from "../model/genericLayer.ts";
import type { AemetProvenance, MitecoOrigin } from "../model/storage.ts";
import type { CalculationOptions } from "../model/calculationOptions.ts";
import type { LoadedProject } from "../model/project.ts";
import type { SubBasinResult } from "../engine/engine.ts";
import type { DrawingTool } from "./drawingTools.ts";
import type { SeriesSelection } from "./mapModes.ts";
import type { ResultSelection } from "./resultSelection.ts";

export const EMPTY_LAYER: SubBasinLayer = { type: "FeatureCollection", features: [], linked: 0 };
export const EMPTY_OUTLET_POINTS_LAYER: OutletPointLayer = {
  type: "FeatureCollection",
  features: [],
  linked: 0,
};
export const EMPTY_AQUIFERS_LAYER: AquiferLayer = {
  type: "FeatureCollection",
  features: [],
  linked: 0,
};

/**
 * App.tsx's whole top-level state — split out here (rather than staying
 * inline in App.tsx) purely so useProjectPersistence.ts (which owns this
 * state via `useState<State>`, see its own header comment) can share the
 * exact same type without App.tsx importing FROM a hook file for it, which
 * would read backwards. App.tsx still does virtually everything else with
 * this type (rendering, the other handler groups) — this file only holds
 * the shape and the one constructor, not any behavior.
 */
export type State =
  | { phase: "initial" }
  | { phase: "loading" }
  | { phase: "creating" }
  | {
      phase: "loaded";
      /** Identity of the IndexedDB record (see storage.ts) — everything that gets edited autosaves there under this id. */
      projectId: string;
      /** Name shown in TopBar.tsx and in "Your projects" (Landing.tsx) — editable there, not here while the project is open. */
      name: string;
      /** Came from an example (see examples.ts) and hasn't been saved as its own copy yet — see ProjectMeta.ephemeral in storage.ts. */
      ephemeral: boolean;
      loadedProject: LoadedProject;
      gisLayer: SubBasinLayer | null;
      /**
       * Outlet points/aquifers with their own geometry — unlike `gisLayer`,
       * there's no loader that brings these in from the real .ehd (that
       * capability doesn't exist yet, see gisLayer.ts): they only grow if the
       * user draws them by hand on the map (see MapDrawing.tsx), so they
       * start empty, never `null`.
       */
      outletPointsLayer: OutletPointLayer;
      aquifersLayer: AquiferLayer;
      /** Purely visual reference layers (points/lines/polygons) uploaded from GenericLayersPanel.tsx — no domain meaning, see genericLayer.ts's own header comment. Always an array, never `null`/`undefined`, same reasoning as `outletPointsLayer`/`aquifersLayer`. */
      genericLayers: GenericLayer[];
      /** Which AEMET station(s) supplied a sub-basin's series, keyed by SubBasinCode — see AemetFetchDialog.tsx/storage.ts. */
      aemetProvenance: Record<number, AemetProvenance>;
      /** Which MITECO layer/code a sub-basin was imported from, keyed by SubBasinCode — see storage.ts/addSelectedMiteco. */
      mitecoOrigin: Record<number, MitecoOrigin>;
      /** Observed daily flow, one column per outlet point name — see storage.ts's own comment. `null` until the user pastes/uploads one from `EditOutletPointDialog`'s "Observado" tab. */
      observedFlow: DailySeries | null;
      results: SubBasinResult[] | null;
      /**
       * Message if `calculateProject()` threw an exception on the last
       * attempt (see `runCalculation`) — unlike `notices`/`blocking`
       * (business rules known before calculating), this is a real engine
       * failure mid-run. Routed to the bell (`TopBar.tsx`/`NoticesPopover.tsx`)
       * instead of taking down the whole app — before this there was no
       * try/catch here, so a failure left a blank screen with no message.
       */
      calculationError: string | null;
      /**
       * Message if the last write to IndexedDB (debounced autosave, "Save
       * copy as…") actually failed — as opposed to `calculationError`, this
       * can mean real, ongoing data loss risk: whatever's in memory right
       * now hasn't reached disk, and refreshing/closing the tab would lose
       * it. Real incident (2026-08-28): at Duero scale (1728 sub-basins,
       * decades of daily data across up to 4 series) a save can throw
       * "Data cannot be cloned, out of memory" — before this field existed,
       * that failure was only a `console.error`, invisible unless devtools
       * happened to be open, so the user only found out much later
       * (reopening the project, or a "Save copy as" silently producing a
       * copy with no series — see `resolveSavedProject`'s own fallback).
       * `null` once the next save succeeds — see `retrySave`/the debounced
       * autosave effect in useProjectPersistence.ts.
       */
      saveError: string | null;
      /**
       * Message if the last successful calculation excluded or truncated one
       * or more sub-basins for having an invalid/empty cell in their own
       * precipitation/ETP series (`engine.ts`'s `findTruncatedSubBasins`) —
       * `null` when nothing was affected, or right after a new run starts.
       * Distinct from `calculationError` (a real engine failure, no results
       * at all): this is a POST-calculation warning — the calculation
       * itself succeeded, just with some sub-basins missing entirely or only
       * partially computed (up to the first bad cell — see engine.ts's
       * `firstInvalidIndex`). Also distinct from `validation.notices`'
       * "no columns" case (a column that doesn't exist at all) — this is a
       * column that exists but has bad data in it. See useCalculation.ts's
       * `runCalculation` and memory `project_evalhid_series_gaps`.
       */
      invalidDataWarning: string | null;
      /** Non-null while `calculateProjectAsync` (engine.ts) is running — how many of `total` sub-basins have been processed so far. `null` the rest of the time, including right after a run finishes. See useCalculation.ts's `runCalculation`. */
      calculationProgress: { done: number; total: number } | null;
      resultsPanelOpen: boolean;
      /** Which sub-basin/outlet point/zone to view in the Results panel — shared with the map (MapResultsMode). */
      resultSelection: ResultSelection | null;
      editorPanelOpen: boolean;
      /**
       * CalibrationPanel.tsx — the same left-hand slot as the editor panel
       * (mutually exclusive with it, see toggleCalibrationPanel/
       * openEditorPanel), opened from its own top-bar button (behind the
       * `?calibrator` URL flag, see featureFlags.ts). Also the real "modo
       * calibración" flag Step3Outlets.tsx's `EditOutletPointDialog` gates
       * its "Serie observada" tab on, replacing Phase 1's provisional gate
       * (whether `onChangeObservedFlow` was passed at all).
       */
      calibrationPanelOpen: boolean;
      /** AemetBulkFetchDialog.tsx — opened from SeriesPanel.tsx's "Get from AEMET" button, which closes the editor panel first (the two stacked together made for a confusing screen, same reasoning as EditSubBasinDialog.tsx's own AEMET button). */
      bulkAemetOpen: boolean;
      /** Pre-scopes the bulk dialog above to just these sub-basin codes (null = the usual "every sub-basin" behavior) — set when it's opened from SeriesPanel.tsx's "gap left over from an extend" notice, so the dialog jumps straight to fixing only the ones actually affected instead of the whole project. */
      bulkAemetOnlyCodes: number[] | null;
      /** Sub-basin selection shared between EditBar and the map/canvas — only takes effect while `editorPanelOpen`. */
      mapSelection: Set<number>;
      /** Sub-basin/aquifer to filter the "Input series" charts by — only takes effect while the "series" editor tab is active (see MapSeriesMode). */
      seriesSelection: SeriesSelection | null;
      /** SubBasinsPanel.tsx's "Add from MITECO" mode (null = off) — only takes effect on the "subBasins" editor tab, and mutually exclusive with drawingMode/mapSelection (see the computed mitecoMode/selectionMode below). */
      mitecoMode: {
        layer: "basins" | "subBasins";
        selected: Map<string, GeoJSON.Feature>;
      } | null;
      /** Active drawing tool on the map (null = off) — see EditBar.tsx/SubBasinMap.tsx. */
      drawingMode: DrawingTool | null;
      /** Geometry already closed (a point, or a polygon after "Finish"), pending the user giving it a name — see NameDrawingDialog.tsx. */
      pendingDrawing: {
        tool: DrawingTool;
        geometry: GeoJSON.Point | GeoJSON.Polygon;
      } | null;
      /** Last confirmed calculation options (dates, aquifers) — null until the first time. Enables the direct recalculate button. */
      calculationOptions: CalculationOptions | null;
      calculationDialogOpen: boolean;
      /**
       * Independent of whether the project has GIS: by default the map is
       * shown if there's a `gisLayer`, but it can be switched to the map
       * even without `gisLayer` (to draw sub-basins from scratch on the
       * base map, without having imported any shapefile) or stay on cards
       * with GIS.
       */
      mapView: boolean;
      /** Dismissible notice that the MITECO wizard tab's automatic AEMET fetch is running in the background — see NewProject.tsx's `onFinishNow`/`finishFromMiteco`. Always starts `false` (see loadedState()) so a later project opened in the same tab never inherits a stale "still running" banner from a previous one. */
      mitecoAemetBannerVisible: boolean;
    }
  | { phase: "error"; message: string };

/**
 * Builds the full "loaded" state from what actually varies depending on
 * where the project came from (a real folder, an example, the "create new"
 * wizard, or a project already saved in IndexedDB) — the rest of the fields
 * (panels closed, no selection, no results yet...) always start the same.
 * Avoids repeating the same giant object in each of those four places.
 */
export function loadedState(data: {
  projectId: string;
  name: string;
  ephemeral: boolean;
  loadedProject: LoadedProject;
  gisLayer: SubBasinLayer | null;
  outletPointsLayer: OutletPointLayer;
  aquifersLayer: AquiferLayer;
  /** Optional: a project saved before this feature existed simply has none. */
  genericLayers?: GenericLayer[];
  aemetProvenance: Record<number, AemetProvenance>;
  mitecoOrigin: Record<number, MitecoOrigin>;
  observedFlow: DailySeries | null;
  mitecoAemetBannerVisible?: boolean;
}): State {
  return {
    phase: "loaded",
    ...data,
    genericLayers: data.genericLayers ?? [],
    mitecoAemetBannerVisible: data.mitecoAemetBannerVisible ?? false,
    results: null,
    calculationError: null,
    saveError: null,
    invalidDataWarning: null,
    calculationProgress: null,
    resultsPanelOpen: false,
    resultSelection: null,
    editorPanelOpen: false,
    calibrationPanelOpen: false,
    bulkAemetOpen: false,
    bulkAemetOnlyCodes: null,
    mapSelection: new Set(),
    seriesSelection: null,
    mitecoMode: null,
    drawingMode: null,
    pendingDrawing: null,
    calculationOptions: null,
    calculationDialogOpen: false,
    // With a layer but no linked sub-basin (e.g. the shapefile didn't match
    // the project's gisCodes) the map wouldn't have anything useful to
    // show — better to open directly on cards, same as without a layer.
    mapView: data.gisLayer !== null && data.gisLayer.linked > 0,
  };
}
